<template>
  <div class="sso-login-page">
    <div id="loadingBox">
      <Loader
        v-if="isLoading"
        :is-processing="true"
        :loader-text="loadingText"
        data-testid="sso-login-loader"
      />
      <p
        v-if="error"
        class="sso-login-page__error"
        role="alert"
        data-testid="sso-login-error"
      >
        {{ error }}
      </p>
    </div>
    <div id="redirectBox" v-if="showRedirectBox">
      <div
        v-if="goToApp || goToVisitorApp"
        class="d-flex flex-column flex-wrap align-items-center gap-4"
      >
        <div class="base-blue-text">
          If you are logging into the Securly Pass App please click here
        </div>
        <a class="btn bg-gradient-blue text-white" :href="goToApp">
          Go To PASS App
        </a>
        <div class="base-blue-text">
          If you are logging into the Securly Visitor App please click here
        </div>
        <a class="btn bg-gradient-blue text-white" :href="goToVisitorApp">
          Go To Visitor App
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, ref } from "vue"
import { useStore } from "vuex"
import { useRoute } from "vue-router"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import localforage from "localforage"
import securlyPlatformConfig from "@/configs/securlyPlatformConfig.js"
import { LOGIN_ROUTE_PATH } from "@/configs/authLoginConfig.js"
import {
  completeWebLogin,
  loadSecurlyPlatformScript
} from "@/helpers/ssoLoginHelper.js"

const randomstring = require("randomstring")

export default {
  name: "SsoLogin",

  components: {
    Loader
  },

  setup() {
    const route = useRoute()
    const store = useStore()
    const loadingText = ref("Loading...")
    const isLoading = ref(true)
    const error = ref(null)
    const ssoRedirected = ref(true)
    const showRedirectBox = ref(false)
    const goToApp = ref(null)
    const goToVisitorApp = ref(null)
    const goToWeb = ref(null)

    const formatSsoLoginError = (err) => {
      if (err?.message) {
        return err.message
      }
      if (Array.isArray(err?.errors) && err.errors.length) {
        return err.errors.join(", ")
      }
      return "Unable to sign in."
    }

    const getRedirectUri = (source = "web", type = null) => {
      const typePath = type ? `/${type}` : ""
      return `${document.location.origin}/${LOGIN_ROUTE_PATH}/${source}${typePath}`
    }

    const getCodeVerifier = (random = true) => {
      let codeVerifier = randomstring.generate(128)

      if (!random) {
        const constantVerifier = import.meta.env.VITE_VERIFIER
        const dateString = new Date()
          .toISOString()
          .slice(0, 10)
          .replace("-", "")
          .replace("-", "")
        codeVerifier = btoa(
          constantVerifier.substring(0, 50) +
            dateString +
            constantVerifier.substring(50)
        ).substring(0, 128)
      }

      sessionStorage.setItem("code_verifier", codeVerifier)
      return codeVerifier
    }

    const afterLoginMobile = (token, err) => {
      const query = token ? "?token=" + token : "?error=" + err
      goToApp.value = import.meta.env.VITE_MOBILE_URL + "login" + query
      goToVisitorApp.value =
        import.meta.env.VITE_VISITOR_MOBILE_URL + "login" + query
      goToWeb.value = document.location.origin + "/login" + query
      showRedirectBox.value = true
    }

    const startSsoLogin = async (forMobile = false) => {
      await localforage.clear().catch(() => null)

      let redirectUri = `${window.location.origin}` + LOGIN_ROUTE_PATH
      if (forMobile) {
        redirectUri = redirectUri + "/mobile"
      }

      let auth = null

      try {
        auth = await loadSecurlyPlatformScript()
        if (!auth) {
          error.value = "Unable to load sign-in."
          isLoading.value = false
          return
        }

        await auth.Init(
          securlyPlatformConfig.clientId,
          securlyPlatformConfig.issuer
        )

        try {
          await auth.Identify()
        } catch {
          loadingText.value = "Redirecting..."
          auth.Login(redirectUri)
          return
        }

        let loginForMobile = route.params.source === "mobile"
        try {
          const user = {
            email: "",
            password: ""
          }
          loadingText.value = "Signing in..."
          const response = await store.dispatch("authentication/login", {
            user,
            isMobile: loginForMobile
          })

          isLoading.value = false
          if (loginForMobile) {
            afterLoginMobile(response?.data?.access_token)
          } else {
            completeWebLogin(store, response?.data)
          }
        } catch (err) {
          error.value = formatSsoLoginError(err)
          isLoading.value = false
          await auth.Logout()
        }
      } catch {
        window.location.reload()
      }
    }

    const startSimulateLogin = async (params) => {
      try {
        const response = await store.dispatch(
          "authentication/externalLogin",
          params
        )
        const data = response?.data

        if (route.params.source === "mobile") {
          afterLoginMobile(data?.access_token)
        } else {
          completeWebLogin(store, data)
        }
        isLoading.value = false
      } catch (err) {
        isLoading.value = false
        if (route.params.source === "mobile") {
          afterLoginMobile(null, err?.message)
        } else {
          error.value = formatSsoLoginError(err)
        }
      }
    }

    onMounted(() => {
      // const referrer = document.referrer

      if (route.query?.mobile_redirect) {
        startSsoLogin(true)
        return
      }

      if (route.query?.code) {
        const routeState = route.query?.state
        // check if the state is string or int then its value should be < 9999
        if (routeState < 9999) {
          // This is simulate login
          ssoRedirected.value = false
        } else {
          // This is normal login
          ssoRedirected.value = true
        }
      } else {
        ssoRedirected.value = true
      }

      if (ssoRedirected.value) {
        startSsoLogin()
        return
      }

      const state = route.query?.state
      isLoading.value = true
      const params = {
        ...route.query,
        redirectUri: getRedirectUri(),
        codeVerifier: sessionStorage.getItem("code_verifier") || null,
        state,
        isMobile: route.params.source === "mobile",
        storedVerifier: state == "20" ? getCodeVerifier(false) : null,
        generatedVerifier: state == "20" ? getCodeVerifier(false) : null
      }
      startSimulateLogin(params)
    })

    return {
      isLoading,
      loadingText,
      error,
      goToApp,
      goToVisitorApp,
      goToWeb
    }
  }
}
</script>

<style scoped lang="scss">
@import "src/assets/scss/_variables.scss";

.sso-login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sso-login-page__error {
  max-width: 420px;
  margin: 0 1rem;
  text-align: center;
  color: $form-red;
}
</style>
